// assistant product init — the conversational assistant's modal (sdk-assistant >= 1.3).
//
// TYPES ONLY: the activation carries the assistant's runtime (`widget` = its ModalWidget,
// `configure` = its own configure(), both set by defineAssistantModal in ../assistant.ts),
// so a customer's widget.js bundles the assistant only when its config defines it — the
// same pattern as the search widgets (products/search.ts).
//
// The assistant keeps its OWN runtime state (separate from search's / rce's configure()),
// so it is configured here, right before create(), with the same resolved URLs
// applyRuntime() gave search + rce (boot() passes resolveRuntime()'s result): chatbot +
// search API (carousel product lookup), image base, the combined shadow stylesheets
// (`stylesUrl` / `stylesUrlNoProduct`: the modal's rules are a part of them,
// scripts/lib/styles.ts ShadowParts) and `bare`. configure() merges, and the dropdown's
// injected chat (`dropdownChat`) shares the same module state, so the values search forwards
// to it stay consistent.
//
// ModalWidget.create(triggers, config, callbacks?) never throws: setup failures go to
// `callbacks.errorHandler` + a StartError event. The try/catch covers configure() and a
// malformed activation; the instance is collected into AssistantInitResult.
//
// EARLY CLICKS (iter 22): boot() installs a guard on the modal's `triggers` before the DOM wait
// (../click-guard.ts installAssistantGuard — it mirrors the widget's own event handling). It is
// released here FIRST, right before configure() + create(), and the first early activation is
// replayed as `open({ opener, keyboard })` one animation frame after create(), at most once.
// open() before the widget's preflight has answered is safe (see ../click-guard.ts): the dialog
// shows its pending state, then the conversation or the unavailable state.

import type { AssistantModalWidget, RaventicSdkConfig } from "../config";
import type { RuntimeUrls } from "../runtime";
import type { AssistantGuard } from "../click-guard";
import { replayNextFrame } from "../click-guard";

type AssistantConfig = NonNullable<RaventicSdkConfig["assistant"]>;

// What the assistant's pipeline (iter 23) fills in place.
export interface AssistantInitResult {
  modal?: ReturnType<typeof AssistantModalWidget.create>;
  errors: Array<{ widget: "modal"; error: unknown }>;
}

// configure()'s argument: Partial<RuntimeConfig> of sdk-assistant.
type AssistantRuntime = Parameters<NonNullable<AssistantConfig["modal"]>["configure"]>[0];

// The assistant's RuntimeConfig subset of the resolved URLs — only defined fields (no
// `undefined` overrides), like applyRuntime()'s per-SDK subsets.
function assistantRuntime(r: RuntimeUrls): AssistantRuntime {
  const out: AssistantRuntime = {};
  if (r.chatbotBaseApiUrl !== undefined) out.chatbotBaseApiUrl = r.chatbotBaseApiUrl;
  if (r.searchBaseApiUrl !== undefined) out.searchBaseApiUrl = r.searchBaseApiUrl;
  if (r.imageBaseUrl !== undefined) out.imageBaseUrl = r.imageBaseUrl;
  if (r.stylesUrl !== undefined) out.stylesUrl = r.stylesUrl;
  if (r.stylesUrlNoProduct !== undefined) out.stylesUrlNoProduct = r.stylesUrlNoProduct;
  if (r.bare !== undefined) out.bare = r.bare;
  return out;
}

// The modal's step (iter 23): boot() runs it in its own pipeline after the modal's readiness
// (today: the shared DOM wait). Never throws: a failure is recorded in `result.errors`, reported
// to `onError` (BootResult.errors) and console.error'd. Undefined when no modal is configured.
export function assistantPipeline(
  cfg: AssistantConfig,
  runtime: RuntimeUrls,
  // boot()'s early-click guard on the modal's triggers, when it installed one.
  guard: AssistantGuard | undefined,
  result: AssistantInitResult,
  onError: (widget: "modal", error: unknown) => void = () => undefined,
): (() => void) | undefined {
  // modal (click): create() binds its delegated trigger listeners and, with a `hide` or
  // `invisible` trigger or a launcher, asks preflight right away; synchronous.
  const modalActivation = cfg.modal;
  if (!modalActivation) return undefined;
  return () => {
    try {
      // Release FIRST (see the header); boot() releases again in a finally.
      const activation = guard?.release() ?? null;
      const { widget, configure, triggers, config, callbacks } = modalActivation;
      configure(assistantRuntime(runtime));
      const modal = widget.create(triggers, config, callbacks);
      result.modal = modal;
      if (activation) {
        replayNextFrame("assistant modal", () => modal.open({ opener: activation.opener, keyboard: activation.keyboard }));
      }
    } catch (error) {
      result.errors.push({ widget: "modal", error });
      onError("modal", error);
      console.error("[@raventic/sdk] assistant modal init failed", error);
    }
  };
}
